import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraSelect } from "../../../components/AtiraSelect";
import { Flex } from "../../../components/Flex";
import i18n from "../../../i18n";
import { GetUserKanbanDto } from "../../../model/kanban/dto/GetUserDefaultKanbanDto";
import { KanbanCardDateField } from "../../../model/kanban/types/KanbanCardDateField.enum";
import { KanbanCardFilterRange } from "../../../model/kanban/types/KanbanCardFilterRange.enum";
import { Radio } from "../../../utils/local-antd";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { getTranslatedKanbanName } from "../KanbanUtils";
import { AtiraDateRange } from "../../../components/AtiraDateRange";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { TestIDs } from "../../../tests/types/testIds";
import { rangeOptions } from "../../../utils/Date";

const StickyFlex = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};

  position: sticky;
  top: 3rem; /* SubHeader is 3rem tall */
  z-index: 90;

  background-color: ${({ theme }) => theme.sub};
  padding: ${Spacing.s} ${Spacing.m};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};

  left: ${i18n.language === AppLangs.AR ? "auto" : "0"};
  right: ${i18n.language === AppLangs.AR ? "0" : "auto"};
`;

const StyledSelect = styled(AtiraSelect)`
  .ant-select .ant-select-selector {
    border: 2px solid ${(props) => props.theme.transparent} !important;
  }

  border: 0;
  border-radius: ${Rounded.md};
`;

const DateFilterGroup = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  border: 1px solid ${({ theme }) => theme.darkSub};
  border-radius: ${Rounded.md};
  padding: ${Spacing.xs};
`;

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    height: 2.3rem;
    display: inline-flex;
    align-items: center;
  }

  .ant-radio-button-wrapper:first-child {
    border-start-start-radius: ${Rounded.md};
    border-end-start-radius: ${Rounded.md};
  }

  .ant-radio-button-wrapper:last-child {
    border-start-end-radius: ${Rounded.md};
    border-end-end-radius: ${Rounded.md};
  }
`;

export const KanbanHeader: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const kanbanLoading = useAppSelector(
    kanbanSliceSelectors.selectUserKanbanLoading,
  );

  const { control, getValues, setValue } = useFormContext<GetUserKanbanDto>();

  const dispatch = useAppDispatch();

  const onFilter = async () => {
    try {
      setLoading(true);
      await dispatch(
        kanbanActions.getUserKanban({
          userId,
          ...omitBy(getValues(), isNil),
        }),
      ).unwrap();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onChangeKanban = async () => {
    try {
      await dispatch(
        kanbanActions.getUserKanban({
          userId,
          kanbanId: getValues("kanbanId"),
        }),
      ).unwrap();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const cardsRangeFitlerOptions = [
    {
      label: t("deals.cards.filters.time.all_deals"),
      value: KanbanCardFilterRange.ALL,
    },
    {
      label: t("deals.cards.filters.time.closing_this_month"),
      value: KanbanCardFilterRange.THIS_MONTH,
    },
    {
      label: t("deals.cards.filters.time.closing_next_month"),
      value: KanbanCardFilterRange.NEXT_MONTH,
    },
  ];

  const dateFieldOptions = [
    {
      label: t("deals.cards.filters.dateField.created"),
      value: KanbanCardDateField.CREATED_AT,
    },
    {
      label: t("deals.cards.filters.dateField.closed"),
      value: KanbanCardDateField.CLOSED_AT,
    },
  ];

  const kanbanOptions =
    kanbansMeta?.map((kanban) => ({
      label: getTranslatedKanbanName(kanban.name),
      value: kanban._id,
      "data-testid": TestIDs.kanbans.subHeader.selectKanban.option(kanban._id),
    })) || [];

  useEffect(() => {
    dispatch(thresholdActions.getThresholds({ userId }));
  }, [dispatch, userId]);

  return (
    <StickyFlex>
      <Flex gap="m" alignItems="center" flexWrap="wrap">
        {kanbansMeta?.length > 1 ? (
          <Controller
            control={control}
            name="kanbanId"
            render={({ field: { onChange, value } }) => (
              <StyledSelect
                value={value}
                options={kanbanOptions}
                onChange={(e) => {
                  onChange(e);
                  onChangeKanban();
                }}
                disabled={kanbanLoading}
                containerStyle={{ width: "15rem" }}
                className={TestIDs.kanbans.subHeader.selectKanban.open}
              />
            )}
          />
        ) : null}

        <Controller
          control={control}
          name="range"
          render={({ field: { onChange, value } }) => (
            <StyledSelect
              value={value}
              options={cardsRangeFitlerOptions}
              onChange={(e) => {
                onChange(e);
                onFilter();
              }}
              disabled={loading}
              containerStyle={{ width: "10rem" }}
            />
          )}
        />

        <DateFilterGroup>
          <Controller
            control={control}
            name="dateRange"
            render={({ field: { onChange, value } }) => (
              <StyledSelect
                value={value ?? null}
                options={rangeOptions(t)}
                onChange={(e) => {
                  onChange(e ?? undefined);
                  setValue("scope", undefined);
                  onFilter();
                }}
                disabled={loading}
                containerStyle={{ width: "10rem" }}
              />
            )}
          />

          <Controller
            control={control}
            name="scope"
            render={({ field: { onChange, value } }) => (
              <AtiraDateRange
                value={value}
                onChange={(datesString) => {
                  onChange(datesString);
                  setValue("dateRange", undefined);
                  onFilter();
                }}
                loading={loading}
              />
            )}
          />

          <Controller
            control={control}
            name="dateField"
            render={({ field: { onChange, value } }) => (
              <StyledRadioGroup
                value={value || KanbanCardDateField.CREATED_AT}
                onChange={(e) => {
                  onChange(e.target.value);
                  onFilter();
                }}
                optionType="button"
                options={dateFieldOptions}
                disabled={loading}
              />
            )}
          />
        </DateFilterGroup>
      </Flex>
    </StickyFlex>
  );
};
